import { useRef, useState } from 'react'
import { ExternalLink, FileText, PenLine, Pencil, Upload, Download, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useUpdateContractReview,
  useContractReviewDocuments,
  useUploadContractReviewDocument,
  useDeleteContractReviewDocument,
} from '@/hooks/use-api'
import { apiClient } from '@/lib/api'
import { explainError } from '@/lib/explain-error'
import type { ContractReview, ContractReviewDocument } from '@/types'

/**
 * The documents on a review: links, and files.
 *
 * A contract does not always begin as a link. It arrives as paper, gets scanned, and what the
 * reviewer needs is the file. Requiring a URL meant the sender had to put it in Drive first and
 * paste the address, which is the manual step this feature exists to remove. So each of the two
 * roles, the draft and the final document, accepts either a link or an upload.
 *
 * This existed as read-only markup first, which made the workflow unfinishable. The state
 * machine refuses `Awaiting signature` and `Signed` unless `signature_document_url` is set, and
 * nothing in the UI could set it, so every review stopped dead at Agreed: the precondition was
 * written and enforced, and the field it guards had no input anywhere in the app.
 *
 * Both links are editable while the review is open, because a draft often arrives after the
 * review is raised and the final document always does. Once the review is signed, declined or
 * withdrawn the form disappears, matching the server, which refuses the write anyway.
 */

const DOC_URL = /^https:\/\/((drive|docs)\.google\.com|([a-z0-9-]+\.)*docusign\.(net|com))\//

interface DocumentLinksProps {
  review: ContractReview
  /** False once the review is settled; the server refuses the write regardless. */
  editable: boolean
}

type Field = 'draft_document_url' | 'signature_document_url'

export function DocumentLinks({ review, editable }: DocumentLinksProps) {
  const update = useUpdateContractReview()
  const [editing, setEditing] = useState<Field | null>(null)
  const [draft, setDraft] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const documents = useContractReviewDocuments(review.id)
  const uploadDoc = useUploadContractReviewDocument()
  const removeDoc = useDeleteContractReviewDocument()
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({})

  const files = documents.data ?? []
  const filesFor = (kind: 'draft' | 'signature') => files.filter((f) => f.document_kind === kind)

  const choose = async (kind: 'draft' | 'signature', file: File | undefined) => {
    if (!file) return
    setProblem(null)
    try {
      await uploadDoc.mutateAsync({ reviewId: review.id, file, kind })
    } catch (err) {
      setProblem(explainError(err, 'The file could not be attached'))
    }
  }

  const remove = async (doc: ContractReviewDocument) => {
    setProblem(null)
    try {
      await removeDoc.mutateAsync({ reviewId: review.id, documentId: doc.id })
    } catch (err) {
      setProblem(explainError(err, 'The file could not be removed'))
    }
  }

  /*
   * Downloaded through the API rather than as a plain link, because the file has no public URL:
   * the backend streams it only after checking the caller is a participant. That means building
   * the blob here and clicking a throwaway anchor.
   */
  const download = async (doc: ContractReviewDocument) => {
    setProblem(null)
    try {
      const { blob, filename } = await apiClient.downloadContractReviewDocument(review.id, doc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setProblem(explainError(err, 'The file could not be downloaded'))
    }
  }

  const open = (field: Field) => {
    setProblem(null)
    setDraft(review[field] ?? '')
    setEditing(field)
  }

  const save = async () => {
    if (!editing) return
    const value = draft.trim()
    if (!value) return setProblem('Paste the link, or press Cancel to leave it as it was.')
    if (!DOC_URL.test(value)) {
      return setProblem('The link must be a Google Drive, Google Docs or DocuSign address.')
    }
    setProblem(null)
    try {
      await update.mutateAsync({ id: review.id, data: { [editing]: value } })
      setEditing(null)
    } catch (err) {
      setProblem(explainError(err, 'The link could not be saved'))
    }
  }

  const row = (field: Field, icon: React.ReactNode, label: string) => {
    const url = review[field]
    if (editing === field) {
      return (
        <div className="space-y-2">
          <Label htmlFor={`doc-${field}`}>{label}</Label>
          <Input
            id={`doc-${field}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://…"
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save link'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditing(null)
                setProblem(null)
              }}
              disabled={update.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex items-center justify-between gap-3">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-primary underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {icon}
            {label}
            <span className="sr-only">, opens in a new tab</span>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            {icon}
            {label}: not added yet
          </span>
        )}
        {editable && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => open(field)}
            /* Named in full, because "Edit" repeated twice in one card tells a screen reader
               user nothing about which link they are about to change. */
            aria-label={url ? `Change the link to the ${label}` : `Add the link to the ${label}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            {url ? 'Change' : 'Add'}
          </Button>
        )}
      </div>
    )
  }

  /** The files attached for one role, plus the control that adds another. */
  const attachments = (kind: 'draft' | 'signature', label: string) => {
    const mine = filesFor(kind)
    const inputId = `upload-${kind}`
    return (
      <div className="space-y-1.5">
        {mine.length > 0 && (
          <ul className="space-y-1" aria-label={`Files attached as the ${label}`}>
            {mine.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => download(doc)}
                  className="flex items-center gap-2 text-primary underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-left"
                >
                  <Download className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="break-all">{doc.file_name}</span>
                  <span className="sr-only">, download this file</span>
                </button>
                {editable && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(doc)}
                    disabled={removeDoc.isPending}
                    aria-label={`Remove ${doc.file_name}`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <div>
            {/* A real file input, labelled and reachable by keyboard, rather than a styled div
                driving a hidden one. The label is the control. */}
            <input
              id={inputId}
              type="file"
              className="sr-only"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              ref={(el) => {
                fileInputs.current[kind] = el
              }}
              onChange={(e) => {
                void choose(kind, e.target.files?.[0])
                e.target.value = ''
              }}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={uploadDoc.isPending}
              onClick={() => fileInputs.current[kind]?.click()}
              aria-label={`Upload a file as the ${label}`}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              {uploadDoc.isPending ? 'Uploading…' : 'Upload a file'}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold">Documents</h2>

      <div className="space-y-1.5">
        {row('draft_document_url', <FileText className="h-4 w-4" aria-hidden="true" />, 'draft')}
        {attachments('draft', 'draft')}
      </div>

      <div className="space-y-1.5">
        {row(
          'signature_document_url',
          <PenLine className="h-4 w-4" aria-hidden="true" />,
          'final document for signature'
        )}
        {attachments('signature', 'final document for signature')}
      </div>
      {/* Said before the transition is attempted rather than as a refusal afterwards. */}
      {/* A file counts, so the hint must not nag when one has been uploaded. */}
      {editable &&
        !review.signature_document_url &&
        filesFor('signature').length === 0 &&
        review.status === 'Agreed' && (
          <p className="text-xs text-muted-foreground">
            Add the final document, as a link or a file, before this can move to awaiting signature.
          </p>
        )}
      {problem && (
        <Alert variant="destructive">
          <AlertDescription>{problem}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}
